import type {RowData} from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import {useCallback, useEffect, useMemo, useState} from 'react';
import type {SWRInfiniteConfiguration} from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

import {createResponseSchema} from './types';
import type {SearchConfig, SearchFetcher, SearchResponse} from './types';

import {
  defaultCheckIsEmpty,
  defaultCheckIsLoadingMore,
  defaultCheckIsReachingEnd,
  defaultCheckIsRefreshing,
  PAGE_SIZE,
} from './helpers';

/**
 * @description A hook that fetches data from a server in pages based on a search query, mostly used for autocomplete
 * @param getKey - A function that returns a SWRInfiniteKeyLoader
 * @param config - A SearchConfig object
 * @returns An object containing the SWRInfiniteResponse, text, setText, search, setSearch, setSearchDebounced, items, isLoadingMore, isEmpty, isReachingEnd, isRefreshing, and isInitialLoading properties
 */
export const useInfiniteSearch = <TData extends RowData = RowData>(
  config: Omit<SWRInfiniteConfiguration<SearchResponse<TData>>, 'fetcher'> &
    SearchConfig & {fetcher: SearchFetcher<TData>; key: string} = {
    fetcher: () => Promise.reject(new Error(`No fetcher provided`)),
    key: `useInfiniteSearch`,
  },
) => {
  const {
    key,
    defaultSearch = null,
    debounce: debounceMs = 200,
    pageSize = PAGE_SIZE,
    checkIsEmpty = defaultCheckIsEmpty,
    checkIsReachingEnd = defaultCheckIsReachingEnd,
    checkIsLoadingMore = defaultCheckIsLoadingMore,
    checkIsRefreshing = defaultCheckIsRefreshing,
    fetcher: configFetcher,
    ...swrConfig
  } = config;
  // Text can be used in inputs, updates immediately
  const [text = defaultSearch, setTextRaw] = useState<string | null | undefined>(undefined);
  // Is what fetcher called with, mainly debounced
  const [search = defaultSearch, setSearch] = useState<string | null | undefined>(undefined);

  const setSearchDebounced = useMemo(
    () =>
      debounce((newSearch: string | null | undefined) => {
        setSearch(newSearch);
      }, debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    return () => {
      setSearchDebounced.cancel();
    };
  }, [setSearchDebounced]);
  const setText = useCallback(
    (newSearch: string | null) => {
      setTextRaw(newSearch);
      setSearchDebounced(newSearch);
    },
    [setTextRaw, setSearchDebounced],
  );

  const getKey = useMemo(
    () =>
      (pageIndex = 0, previousPageData: SearchResponse<TData> | null) => {
        // Prevent default fetching
        if (search === null) return null;

        // Stop condition
        if (previousPageData?.data) {
          const {has_more} = previousPageData.data;
          if (typeof has_more === `boolean` && !has_more) {
            return null;
          }
        }

        return {
          key,
          search,
          pageIndex,
          pageSize,
          offset: pageIndex * pageSize,
        };
      },
    [search, pageSize, key],
  );

  const fetcher = useCallback(
    async (args: ReturnType<typeof getKey>) => {
      if (!args) return Promise.reject(new Error(`No args provided`));
      const {pageIndex, pageSize, search} = args ?? {};
      if (!configFetcher) return Promise.reject(new Error(`No fetcher provided`));

      const response = await configFetcher(search, {
        pageIndex,
        pageSize,
        offset: pageIndex * pageSize,
        search,
      });
      const validatedResponse = createResponseSchema<TData>().parse(response);
      return validatedResponse;
    },
    [configFetcher],
  );

  const swrQuery = useSWRInfinite(getKey, fetcher, {
    initialSize: 1,
    keepPreviousData: true,
    ...swrConfig,
  });

  const {data, isLoading, isValidating, size} = swrQuery;
  const {items, isLoadingMore, isEmpty, isReachingEnd, isRefreshing, isInitialLoading} = useMemo(() => {
    const items = (data || [])
      .map(d => d.data?.items)
      .flat()
      .filter(Boolean);

    const isLoadingMore = checkIsLoadingMore(isLoading, data, size);
    const isEmpty = checkIsEmpty(data);
    const isReachingEnd = checkIsReachingEnd(data, isEmpty, pageSize);
    const isRefreshing = checkIsRefreshing(isValidating, data, size);
    const isInitialLoading = data === undefined && isLoading;

    return {items, isLoadingMore, isEmpty, isReachingEnd, isRefreshing, isInitialLoading};
  }, [
    data,
    isLoading,
    isValidating,
    size,
    checkIsLoadingMore,
    checkIsEmpty,
    checkIsReachingEnd,
    checkIsRefreshing,
    pageSize,
  ]);

  return {
    ...swrQuery,
    text,
    setText,
    search,
    setSearch,
    setSearchDebounced,
    items,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    isInitialLoading,
  };
};
